import { useMutation, type UseMutationResult } from "@tanstack/react-query";

import { KeywordGroupEditorHelpers } from "app/pages/keyword-analysis/KeywordGroupEditorHelpers";
import type { IUserKeywordGroup } from "app/services/keyword-groups";
import { keywordGroupsOptions, keywordGroupsQueryClient, useCreateKeywordGroup } from "app/services/keyword-groups";
import queryClient from "single-spa/react-app/query-client";

export interface ICreateKeywordListFromGoogleAdsVariables {
    name: string;
    keywords: string[];
}

export const PLAN_KEYWORD_LIMIT_ERROR_MESSAGE =
    "This ad group has more keywords than your plan supports. Reduce the number of keywords or upgrade your plan to import it.";

export const GENERIC_CREATE_LIST_ERROR_MESSAGE = "Failed to create the keyword list.";

const BAD_REQUEST_STATUS = 400;

// The generic create/update pipeline throws whatever `getResponseError` produces (see
// app/services/fetchService/defaults.ts), which always carries the HTTP status and, when the
// backend sent a problem+json body, `problem.detail` / `problem.title` — even though its declared
// type is the generic `Error`.
interface IRequestError {
    status?: unknown;
    problem?: { detail?: string; title?: string };
}

const isBadRequestError = (error: unknown): boolean =>
    typeof error === "object" && error !== null && (error as IRequestError).status === BAD_REQUEST_STATUS;

const getServerMessage = (error: unknown): string => {
    const problem = (error as IRequestError).problem;
    return problem?.detail || problem?.title || GENERIC_CREATE_LIST_ERROR_MESSAGE;
};

// The create endpoint documents a bare 400 with no body or error code, so the plan limit can only
// be recognised by the import exceeding the plan's maximum keywords per list.
const exceedsPlanKeywordLimit = (keywordCount: number): boolean =>
    keywordCount > KeywordGroupEditorHelpers.getMaxGroupCount();

// The legacy create hook resolves with the first list matching the requested NAME, which can be a
// pre-existing list (or one created concurrently). Instead, identify the created list by the id
// that appeared after the create.
const fetchFreshUserGroupIds = async (): Promise<Set<string>> => {
    const groups: IUserKeywordGroup[] = await queryClient.fetchQuery({ ...keywordGroupsOptions.user(), staleTime: 0 });
    return new Set(groups.map((group) => group.Id));
};

/**
 * Creates a new Keyword List from Google Ads ad group data (SIM-107).
 *
 * Returns the created `IUserKeywordGroup` (including its `Id`) so the caller can navigate
 * straight to the new list's detail page. SIM-108 should hook into this same `Id` — either
 * from the resolved `mutateAsync` value or the `onSuccess` callback passed via `options` — to
 * persist it for the "new list" badge, without needing any changes to this hook.
 */
export const useCreateKeywordListFromGoogleAds = (options?: {
    onSuccess?: (createdGroup: IUserKeywordGroup) => void;
}): UseMutationResult<IUserKeywordGroup, Error, ICreateKeywordListFromGoogleAdsVariables> => {
    const createKeywordGroup = useCreateKeywordGroup();

    return useMutation({
        mutationFn: async ({ name, keywords }: ICreateKeywordListFromGoogleAdsVariables) => {
            const idsBeforeCreate = await fetchFreshUserGroupIds();

            try {
                await createKeywordGroup.mutateAsync({ Name: name, Keywords: keywords });
            } catch (error) {
                if (isBadRequestError(error)) {
                    throw new Error(
                        exceedsPlanKeywordLimit(keywords.length)
                            ? PLAN_KEYWORD_LIMIT_ERROR_MESSAGE
                            : getServerMessage(error),
                    );
                }
                throw error;
            }

            // The legacy hook has already refetched the user's lists, so this is served from the fresh cache.
            const groupsAfterCreate = await keywordGroupsQueryClient.getUserGroups();
            const newGroups = groupsAfterCreate.filter((group) => !idsBeforeCreate.has(group.Id));
            const createdGroup = newGroups.length === 1 ? newGroups[0] : undefined;

            if (!createdGroup) {
                throw new Error("The keyword list was created but could not be located afterwards.");
            }

            return createdGroup;
        },
        onSuccess: (createdGroup) => options?.onSuccess?.(createdGroup),
    });
};
